<script setup>
import Check from '~icons/hawk/check?raw';
import Cross from '~icons/hawk/cross?raw';
import Plus from '~icons/hawk/plus?raw';
import PlusCircle from '~icons/hawk/plus-circle?raw';
import Trash from '~icons/hawk/trash-one?raw';
import { onMounted } from 'vue';
import BiHandsontable from '~/bi/components/widgets/table-widget/bi-handsontable.vue';
import { changeIconDimensions } from '~/common/utils/common.utils.js';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    default: () => [],
  },
  columnOptions: {
    type: Array,
    default: () => [], // Array of objects with key and value
  },
});

const state = reactive({
  is_loading: false,
  data: [{}, {}, {}],
  columns: [
    {
      data: crypto.randomUUID(),
      header: '',
      type: 'text',
      readOnly: false,
    },
    {
      data: 'add-columns',
      header: '',
      readOnly: true,
      width: 40,
      is_add: true,
    },
  ],
  current_title: '',
  is_editing_header: null,
  hot_instance: null,
});

function handleColumnResize(columns_widths) {
  state.columns = state.columns.map((col, idx) => {
    col.width = columns_widths[idx];
    return col;
  });
}

function renderEditableHeader(TH, col, instance) {
  const col_state = state.columns[col];
  const current_title = col_state.header;

  TH.innerHTML = '';
  TH.style.padding = '8px 16px';
  TH.style.backgroundColor = '#F9FAFB !important';
  TH.style.height = '36px';
  TH.style.minWidth = '50px';
  TH.classList.add('group');

  if (state.is_editing_header === col) {
    TH.style.padding = '4px 16px';
    const input = document.createElement('input');
    input.value = current_title;
    input.classList.add(
      'text-sm',
      'w-full',
      'h-full',
      'border-none',
      'outline-none',
      'bg-transparent',
      'h-[26px]',
      '!text-gray-700',
    );

    const cancel_btn = document.createElement('span');
    cancel_btn.classList.add('text-gray-500', 'cursor-pointer');
    cancel_btn.innerHTML = changeIconDimensions(Cross, 10, 10);

    const save_btn = document.createElement('span');
    save_btn.classList.add('text-primary-500', 'cursor-pointer');
    save_btn.innerHTML = changeIconDimensions(Check, 18, 18);

    const btn_container = document.createElement('div');
    btn_container.classList.add('flex', 'items-center', 'space-x-2', 'px-2');
    btn_container.appendChild(cancel_btn);
    btn_container.appendChild(save_btn);

    const container = document.createElement('div');
    container.classList.add(
      'flex',
      'items-center',
      'justify-between',
      'border',
      'bg-white',
      'h-[26px]',
      'pl-2',
      'rounded',
    );
    container.appendChild(input);
    container.appendChild(btn_container);

    TH.appendChild(container);

    input.focus();

    // Keyboard shortcuts
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter')
        save_btn.click();
      if (e.key === 'Escape')
        cancel_btn.click();
    });

    // CANCEL
    cancel_btn.onclick = () => {
      state.columns[col].header = state.current_title;
      state.is_editing_header = null;
      instance.render();
    };

    // SAVE
    save_btn.onclick = () => {
      state.columns[col].header = input.value.trim();
      state.is_editing_header = null;
      instance.render();
    };

    return;
  }

  if (col_state.is_add) {
    TH.innerHTML = '';
    const container = document.createElement('div');
    container.classList.add('flex', 'items-center', 'justify-center', 'h-full', 'w-full');
    const add_btn = document.createElement('span');
    add_btn.innerHTML = changeIconDimensions(PlusCircle, 18, 18);
    add_btn.classList.add('cursor-pointer', 'flex', 'items-center', 'justify-center');
    add_btn.onclick = (e) => {
      e.stopPropagation();
      state.columns.splice(state.columns.length - 1, 0, {
        data: crypto.randomUUID(),
        header: '',
        type: 'autocomplete',
        readOnly: false,
        source: props.columnOptions,
        filter: false,
        strict: true,
        trimDropdown: true,
      });
      instance.updateSettings({ columns: state.columns });
    };
    container.appendChild(add_btn);
    TH.appendChild(container);
    return;
  }

  const container = document.createElement('div');
  container.classList.add('flex', 'items-center', 'justify-between', 'h-full', 'w-full');

  const label = document.createElement('div');
  let col_index = TH.getAttribute('aria-colindex');
  col_index = col_index ? Number(col_index) : null;
  const default_title = col_index > 1 ? `Value ${col_index - 1}` : 'Attribute';
  label.textContent = current_title || default_title;
  label.classList.add('w-full', 'flex', 'items-center', 'justify-start', 'cursor-text');

  const delete_icon = document.createElement('span');
  delete_icon.innerHTML = changeIconDimensions(Trash, 16, 16);
  delete_icon.classList.add('cursor-pointer', 'text-red-500', 'flex', 'items-center', 'justify-center', 'invisible', 'group-hover:visible');
  delete_icon.onclick = () => {
    state.columns.splice(col, 1);
    instance.updateSettings({ columns: state.columns });
  };

  container.appendChild(label);
  if (col !== 0) {
    container.appendChild(delete_icon);
  }

  TH.appendChild(container);

  // click to start editing
  label.onclick = () => {
    state.current_title = current_title;
    state.is_editing_header = col;
    instance.render();
  };

  TH.onclick = (e) => {
    e.stopImmediatePropagation();
  };
}

function additionalTableSettings() {
  return {
    fillHandle: true,
    pagination: false,
    // selectionMode: 'cell',
    autoColumnSize: false,
    fixedColumnsRight: 1,
    afterGetColHeader(col, TH) {
      // eslint-disable-next-line ts/no-this-alias
      const instance = this;
      renderEditableHeader(TH, col, instance);
    },
    cells(row, col) {
      const cellProperties = {};
      const last_row_index = this?.instance?.countRows() - 1;
      if (col === state.columns.length - 1 || row === last_row_index)
        cellProperties.readOnly = true;
      if (col === state.columns.length - 1) {
        cellProperties.renderer = (instance, td, row) => {
          td.innerHTML = '';

          const delete_icon = document.createElement('span');
          delete_icon.innerHTML = changeIconDimensions(Trash, 18, 18);
          delete_icon.classList.add('cursor-pointer', 'text-red-500', 'flex', 'items-center', 'justify-center', 'mr-2');
          delete_icon.onclick = () => {
            instance.alter('remove_row', row, 1);
          };
          td.appendChild(delete_icon);
        };
      }
      if (row === last_row_index) {
        cellProperties.renderer = (instance, td, row, col) => {
          td.innerHTML = '';
          if (col === 0) {
            const container = document.createElement('div');
            container.classList.add('flex', 'items-center', 'justify-start', 'h-full', 'w-full', 'cursor-pointer');
            const add_icon = document.createElement('span');
            add_icon.innerHTML = changeIconDimensions(Plus, 16, 16);
            add_icon.classList.add('w-4', 'h-4');

            const label = document.createElement('div');
            label.textContent = 'Add batch';
            label.classList.add('text-[13px]', 'text-gray-700', 'ml-1.5', 'font-medium');
            container.appendChild(add_icon);
            container.appendChild(label);

            container.onclick = () => {
              instance.alter('insert_row_above', last_row_index, 1);
            };

            td.appendChild(container);
          }
        };
      }
      if (!cellProperties.readOnly) {
        cellProperties.validator = (value, callback) => {
          const column_details = state.columns[col];

          if (column_details.type === 'autocomplete') {
            const final_value = value?.value || value;
            if (props.columnOptions.filter(opt => opt.value === final_value && opt.key === final_value).length === 1) {
              callback(true);
            }
            else {
              if (final_value === null)
                callback(true);
              else
                callback(false);
            }
          }
          else if (column_details.type === 'text') {
            callback(value.length > 0);
          }
        };
      }
      return cellProperties;
    },
    beforeOnCellMouseDown(event, coords) {
      if (coords.row === -1) {
        event.stopImmediatePropagation();
        return false;
      }
    },
  };
}

async function validate_table() {
  return new Promise((resolve) => {
    state.hot_instance?.validateCells((valid) => {
      resolve(valid);
    });
  });
}

async function getData() {
  const columns = state.columns.filter(col => !col.is_add).map((col, index) => {
    if (index === 0)
      col.header = col.header || 'Attributes';
    else
      col.header = col.header || `Values ${index}`;
    return col;
  });
  const data = state.hot_instance.getSourceData();
  const is_valid = await validate_table();
  if (!is_valid) {
    throw new Error('Invalid data');
  }
  return { columns, data };
}

onMounted(() => {
  state.is_loading = true;
  if (props.data.length && props.columns.length) {
    state.data = props.data;
    state.data.push({});
    state.columns = props.columns;
    state.columns.push({
      data: 'add-columns',
      header: '',
      readOnly: true,
      width: 40,
      is_add: true,
    });
  }
  state.is_loading = false;
});

defineExpose({
  getData,
});
</script>

<template>
  <div class="mt-4">
    <div class="text-sm font-semibold text-gray-900 mb-1.5">
      {{ $t('Configure unpivot') }}
    </div>
    <div class="text-xs text-gray-500 mb-2">
      {{ $t('You can define final columns, row titles and map them to current columns.') }}
    </div>
    <BiHandsontable
      v-if="!state.is_loading"
      bi-table-id="bi-unpivot-advanced-configuration"
      :height="230"
      :data="state.data"
      :columns="state.columns"
      :row-headers="false"
      :additional-table-settings="additionalTableSettings()"
      @column-resize="handleColumnResize"
      @table-instance="state.hot_instance = $event.hotInstance"
    />
  </div>
</template>

<style scoped lang="scss">

</style>
